import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { first } from 'rxjs';
import { AiOrderParserSettings } from '../../../models/ai-order-parser-settings';
import { PdfAiParserService } from '../../../services/pdf-ai-parser/pdf-ai-parser.service';
import { CanDeactivateComponent } from '../../can-deactivate.component';

/**
 * Admin-only settings screen for the AI order parser. Loads the DB-config-backed
 * settings, edits the boolean toggles, and saves the whole object back (round-tripping
 * `@class`). Modeled on pdf-ai-parser-config-edit.
 */
@Component({
  selector: 'lib-pdf-ai-parser-settings',
  standalone: false,
  templateUrl: './pdf-ai-parser-settings.component.html',
  styleUrl: './pdf-ai-parser-settings.component.scss'
})
export class PdfAiParserSettingsComponent extends CanDeactivateComponent implements OnInit {
  settings?: AiOrderParserSettings;
  /** JSON snapshot of the last-saved (or freshly-loaded) settings, for the dirty check. */
  private savedSnapshot = '';
  loading = false;
  saving = false;
  errorMessage = '';
  newForwarder = '';
  newRecipient = '';
  recipientInvalid = false;

  private pdfAiParserService = inject(PdfAiParserService);
  private notificationService = inject(GungNotificationService);
  private translateService = inject(TranslateService);

  ngOnInit(): void {
    this.loading = true;
    this.pdfAiParserService.getSettings().pipe(first()).subscribe({
      next: settings => {
        this.settings = {
          ...settings,
          trustedForwarders: settings.trustedForwarders || [],
          notificationRecipients: settings.notificationRecipients || []
        };
        this.savedSnapshot = this.snapshot();
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'AI_PARSER_ERROR_LOAD_SETTINGS';
        this.loading = false;
      }
    });
  }

  /** Cleared/zero/negative/fractional input: the backend int would silently store 0. */
  get thresholdInvalid(): boolean {
    const value = this.settings?.manualReviewThreshold;
    return value == null || !Number.isInteger(value) || value < 1;
  }

  save(): void {
    if (!this.settings || this.saving || this.thresholdInvalid) {
      return;
    }
    this.errorMessage = '';
    this.saving = true;
    this.pdfAiParserService.updateSettings(this.settings).pipe(first()).subscribe({
      next: saved => {
        this.settings = {
          ...saved,
          trustedForwarders: saved.trustedForwarders || [],
          notificationRecipients: saved.notificationRecipients || []
        };
        this.savedSnapshot = this.snapshot();
        this.saving = false;
        this.notificationService.notify(
          '', this.translateService.instant('SAVED'), 'success');
      },
      error: () => {
        this.saving = false;
        this.errorMessage = 'AI_PARSER_ERROR_SAVE_SETTINGS';
      }
    });
  }

  addForwarder(): void {
    if (!this.settings) {
      return;
    }
    const trimmed = this.newForwarder.trim().toLowerCase();
    if (trimmed && !this.settings.trustedForwarders.includes(trimmed)) {
      this.settings.trustedForwarders = [...this.settings.trustedForwarders, trimmed];
    }
    this.newForwarder = '';
  }

  removeForwarder(forwarder: string): void {
    if (!this.settings) {
      return;
    }
    this.settings.trustedForwarders = this.settings.trustedForwarders.filter(f => f !== forwarder);
  }

  addRecipient(): void {
    if (!this.settings) {
      return;
    }
    const trimmed = this.newRecipient.trim().toLowerCase();
    // A recipient must be a full address; a bare domain can't be mailed.
    this.recipientInvalid = !!trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (this.recipientInvalid) {
      return;
    }
    if (trimmed && !this.settings.notificationRecipients.includes(trimmed)) {
      this.settings.notificationRecipients = [...this.settings.notificationRecipients, trimmed];
    }
    this.newRecipient = '';
  }

  removeRecipient(recipient: string): void {
    if (!this.settings) {
      return;
    }
    this.settings.notificationRecipients = this.settings.notificationRecipients.filter(r => r !== recipient);
  }

  /** Guard hook: false blocks tab/link navigation behind a confirm, and arms beforeunload. */
  canDeactivate(): boolean {
    return !this.settings || this.snapshot() === this.savedSnapshot;
  }

  /**
   * Only the editable fields — `@class`/`id` round-trip unchanged and a typed-but-not-added
   * forwarder/recipient is not a change to the settings themselves.
   */
  private snapshot(): string {
    return JSON.stringify({
      allowInvalidRowOverride: this.settings?.allowInvalidRowOverride,
      checkAvailability: this.settings?.checkAvailability,
      pickStockCoveringMostRows: this.settings?.pickStockCoveringMostRows,
      autoSubmitEnabled: this.settings?.autoSubmitEnabled,
      manualReviewThreshold: this.settings?.manualReviewThreshold,
      trustedForwarders: this.settings?.trustedForwarders,
      notificationRecipients: this.settings?.notificationRecipients
    });
  }
}
